import { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { environment } from '../enviroment/environment';

export interface IRequestOptions {
  headers?: Record<string, string>;
  apiRoot?: string;
  requireAuth?: boolean;
}

export class ApiService {
  private axiosInstance: AxiosInstance;
  private apiRoot: string;

  constructor(axiosInstance: AxiosInstance, apiRoot?: string) {
    this.axiosInstance = axiosInstance;
    this.apiRoot = apiRoot || environment.api.apiRootServer;
    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token && config.headers) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          this.clearAuthData();
          window.location.href = '/auth';
        }
        return Promise.reject(error);
      }
    );
  }

  private getToken(): string | null {
    try {
      return localStorage.getItem('authToken');
    } catch (error) {
      return null;
    }
  }

  private clearAuthData(): void {
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
    } catch (error) {
    }
  }

  private getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private getApiRootAndHeaders(options?: IRequestOptions): IRequestOptions {
    const baseHeaders = options?.requireAuth !== false ? this.getAuthHeaders() : { 'Content-Type': 'application/json' };
    
    return {
      apiRoot: options?.apiRoot || this.apiRoot,
      headers: {
        ...baseHeaders,
        ...options?.headers,
      },
    };
  }

  async postRequest<T>(url: string, body: any, options?: IRequestOptions): Promise<T> {
    try {
      const { apiRoot, headers } = this.getApiRootAndHeaders(options);
      
      const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
      const response: AxiosResponse<T> = await this.axiosInstance.post(`${apiRoot}/${cleanUrl}`, body, {
        headers,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getRequest<T>(url: string, params?: Record<string, any>, options?: IRequestOptions): Promise<T> {
    try {
      const { apiRoot, headers } = this.getApiRootAndHeaders(options);
      
      const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
      const fullUrl = new URL(`${apiRoot}/${cleanUrl}`);
      
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          fullUrl.searchParams.append(key, String(value));
        }
      });

      const response: AxiosResponse<T> = await this.axiosInstance.get(fullUrl.toString(), {
        headers,
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async get<T>(url: string, options?: IRequestOptions): Promise<T> {
    return this.getRequest<T>(url, undefined, options);
  }

  async putRequest<T>(url: string, body: any, options?: IRequestOptions): Promise<T> {
    try {
      const { apiRoot, headers } = this.getApiRootAndHeaders(options);
      
      const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
      const response: AxiosResponse<T> = await this.axiosInstance.put(`${apiRoot}/${cleanUrl}`, body, {
        headers,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async patchRequest<T>(url: string, body: any, options?: IRequestOptions): Promise<T> {
    try {
      const { apiRoot, headers } = this.getApiRootAndHeaders(options);
      
      const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
      const response: AxiosResponse<T> = await this.axiosInstance.patch(`${apiRoot}/${cleanUrl}`, body, {
        headers,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteRequest<T>(url: string, options?: IRequestOptions): Promise<T> {
    try {
      const { apiRoot, headers } = this.getApiRootAndHeaders(options);
      
      const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
      const response: AxiosResponse<T> = await this.axiosInstance.delete(`${apiRoot}/${cleanUrl}`, {
        headers,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}